import axios from 'axios';
import { API_URL } from '../../../../../config';

// Funkcija koja ekstrahira pravi ID iz stringificiranog objekta djelatnika
const normalizeEmployeeId = (employeeId: string): string => {
  // Provjeri je li ID zapravo stringificirani objekt
  if (employeeId.includes('ObjectId')) {
    // Pokušaj izvući ID iz stringa
    const match = employeeId.match(/ObjectId\('([^']+)'\)/);
    if (match && match[1]) {
      return match[1];
    }
  }
  return employeeId;
};

export interface AttendanceRecord {
  date: string;
  present: boolean;
  absenceReason?: string; // 'sick-leave', 'vacation', 'other'
  absenceComment?: string;
}

export interface EmployeeAttendance {
  employeeId: string;
  employeeName: string;
  records: Record<string, AttendanceRecord>;
}

export interface AttendanceResponse {
  locationId: string;
  locationName: string;
  startDate: string;
  endDate: string;
  employees: EmployeeAttendance[];
}

export const attendanceAPI = {
  getLocationAttendance: async (locationId: string, startDate: string, endDate: string, token: string) => {
    try {
      // Ovdje bi inače bio stvarni API poziv, ali za sada ćemo koristiti API za statistiku
      // i pretvoriti podatke u format koji nam treba za evidenciju prisutnosti
      const response = await axios.get(`${API_URL}/location-stats/${locationId}/employees/stats`, {
        params: { startDate, endDate },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Pretvaramo podatke iz statistike u podatke o prisutnosti
      const statsData = response.data;
      
      
      // Prvo grupiramo djelatnike po normaliziranom ID-u kako bismo uklonili duplikate
      const employeeMap = new Map<string, any>();
      
      // Grupiramo djelatnike po normaliziranom ID-u i uzimamo onaj s danima (ako postoji)
      statsData.employees.forEach((emp: any) => {
        const normalizedId = normalizeEmployeeId(emp.employeeId);
        
        // Ako već imamo djelatnika s istim ID-om, uzimamo onaj s više podataka (s danima)
        if (employeeMap.has(normalizedId)) {
          const existing = employeeMap.get(normalizedId);
          // Preferiramo djelatnika koji ima dane ili više dana
          if (!existing.days || (emp.days && emp.days.length > (existing.days?.length || 0))) {
            employeeMap.set(normalizedId, emp);
          }
        } else {
          employeeMap.set(normalizedId, emp);
        }
      });
      
      // Sada pretvaramo mapu u niz jedinstvenih djelatnika
      const uniqueEmployees = Array.from(employeeMap.values());
      
      // Pretvaramo podatke u format za evidenciju prisutnosti
      const employees: EmployeeAttendance[] = uniqueEmployees.map((emp: any) => {
        const records: Record<string, AttendanceRecord> = {};
        
        // Za svakog zaposlenika, pregledavamo dane i bilježimo prisutnost
        if (emp.days && Array.isArray(emp.days)) {
          emp.days.forEach((day: any) => {
            // Ako zaposlenik ima zabilježene minute rada za taj dan, smatramo ga prisutnim
            const present = day.totalWorkMinutes > 0;
            
            records[day.date] = {
              date: day.date,
              present,
              // Ako nije prisutan, dodajemo prazan razlog koji se može kasnije urediti
              ...(present ? {} : {
                absenceReason: 'other',
                absenceComment: ''
              })
            };
          });
        }
        
        return {
          employeeId: normalizeEmployeeId(emp.employeeId),
          employeeName: emp.employeeName,
          records
        };
      });
      
      return {
        locationId: statsData.locationId,
        locationName: statsData.locationName,
        startDate: statsData.startDate,
        endDate: statsData.endDate,
        employees
      } as AttendanceResponse;
    } catch (error) {
      console.error('Greška pri dohvaćanju podataka o prisutnosti:', error);
      throw error;
    }
  }
};
